package fr.frux.feature.map

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_first_contribution_add_another
import fr.frux.feature.map.resources.map_first_contribution_body
import fr.frux.feature.map.resources.map_first_contribution_first_here
import fr.frux.feature.map.resources.map_first_contribution_photo_failed
import fr.frux.feature.map.resources.map_first_contribution_see_on_map
import fr.frux.feature.map.resources.map_first_contribution_title
import org.jetbrains.compose.resources.stringResource

/**
 * Premier arbre de la personne (spec onboarding de contribution §4). [firstHere] : la zone était
 * vide avant l'ajout. [photoUploadFailed] : la photo reste à renvoyer depuis la fiche, dit ici
 * plutôt que de remplacer la célébration par le snackbar d'échec.
 */
data class FirstContributionCelebration(
    val firstHere: Boolean,
    val photoUploadFailed: Boolean,
)

/** Ni note, ni don, ni partage : le moment appartient entièrement à la contribution (spec §4). */
@Composable
internal fun FirstContributionContent(
    celebration: FirstContributionCelebration,
    onSeeOnMap: () -> Unit,
    onAddAnother: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        Text(
            text = stringResource(Res.string.map_first_contribution_title),
            style = MaterialTheme.typography.titleLarge,
        )
        Text(
            text = stringResource(Res.string.map_first_contribution_body),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        if (celebration.firstHere) {
            Surface(
                shape = MaterialTheme.shapes.small,
                color = MaterialTheme.colorScheme.primaryContainer,
            ) {
                Text(
                    text = stringResource(Res.string.map_first_contribution_first_here),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                    modifier = Modifier.padding(horizontal = FruxSpacing.sm, vertical = FruxSpacing.xs),
                )
            }
        }
        if (celebration.photoUploadFailed) {
            Text(
                text = stringResource(Res.string.map_first_contribution_photo_failed),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.error,
            )
        }
        FruxButton(
            text = stringResource(Res.string.map_first_contribution_see_on_map),
            onClick = onSeeOnMap,
            style = FruxButtonStyle.Filled,
            modifier = Modifier.fillMaxWidth(),
        )
        FruxButton(
            text = stringResource(Res.string.map_first_contribution_add_another),
            onClick = onAddAnother,
            style = FruxButtonStyle.Outline,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

/** Fermer la feuille revient à « Voir sur la carte » : la caméra est déjà sur l'arbre. */
@Composable
internal fun FirstContributionSheet(
    celebration: FirstContributionCelebration,
    onSeeOnMap: () -> Unit,
    onAddAnother: () -> Unit,
) {
    FruxModalBottomSheet(onDismiss = onSeeOnMap, skipPartiallyExpanded = true) {
        FirstContributionContent(
            celebration = celebration,
            onSeeOnMap = onSeeOnMap,
            onAddAnother = onAddAnother,
            modifier = Modifier.padding(horizontal = FruxSpacing.md),
        )
        Spacer(Modifier.height(FruxSpacing.lg))
    }
}
